import {createContext, useContext, useState, useEffect} from "react";
import {useApp} from "./AppDataContext";
import {useNavigate} from "react-router-dom";
import {jwtDecode} from "jwt-decode";
import axios from "axios";
import useLocalStorage from "../hooks/useLocalStorage";


const AuthContext = createContext(null);

function AuthProvider({children}) {
    const [user, setUser] = useLocalStorage("user", null);
    const [isAuthenticated, setIsAuthenticated] = useState(!!user);
    const {setIsLoading, apiUrl, error, success} = useApp();
    const navigate = useNavigate();
    axios.defaults.baseURL = apiUrl;
    axios.defaults.headers.common['Content-Type'] = 'application/json';
    axios.defaults.headers.common['Accept'] = 'application/json';

    // Sync authentication state
    useEffect(() => {
        setIsAuthenticated(!!user);
    }, [user]);


    // Refresh token only if it's expired
    useEffect(() => {
        if (!user?.access_token) return;
        const decoded = jwtDecode(user.access_token);
        const expiryTime = decoded.exp * 1000;
        const currentTime = Date.now();

        if (expiryTime < currentTime) {
            refresh();
        }
    }, [user?.access_token]);

    function login(email, password) {
        setIsLoading(true); // Set loading to true when request starts
        axios.post(`/auth/login`, {email, password})
            .then((res) => {
                const tokens = {
                    access_token: res.data.access_token,
                    refresh_token: res.data.refresh_token,
                };
                const userData = jwtDecode(tokens.access_token);
                setUser({...tokens, ...userData, fetched: false}); // Update user state
                success("Login successful!");
            })
            .catch((err) => {
                error("Invalid email or password");
                console.error("Login error:", err.response?.data || err.message);
            })
            .finally(() => {
                setIsLoading(false); // Set loading to false only after everything is done
            });
    }

    function logout() {
        setIsLoading(true);
        const config = {
            headers: {Authorization: `Bearer ${user?.access_token}`},
        };
        axios.post('/auth/logout', null, config)
            .then(() => {
                success("Logout successful!");
            })
            .catch((err) => {
                error("An error occurred during logout");
                console.error(err);
            })
            .finally(() => {
                setUser(null);
                setIsAuthenticated(false);
                navigate("/", {replace: true});
                setIsLoading(false)
            });
    }

    function signUp(email, username, password) {
        const singUpData = {
            email,
            username,
            password,
        }
        setIsLoading(true);
        const config = {
            "Content-Type": "application/json",
        };
        axios.post('/auth/signup', singUpData, config)
            .then((res) => {
                if (res.status === 201) {
                    success("Signup successfully!");
                    navigate("/login", {replace: true});
                }
            })
            .catch((err) => {
                console.log(err);
                if (err.status === 400) {
                    error("Signup failed, username or email already exists");
                    navigate("/login", {replace: true});
                } else {
                    error("Signup failed, please contact support!")
                }
            }).finally((res) => {
            // Set isLoading to false after the request completes
            setIsLoading(false);
        });
    }

    function refresh() {
        setIsLoading(true);
        axios.post('/auth/refresh', null, {
            headers: {Authorization: `Bearer ${user?.refresh_token}`},
        })
            .then((res) => {
                setUser((prev) => ({...prev, access_token: res.data.access_token}));
                success("Token refreshed successfully!");
            })
            .catch((err) => {
                error("Session expired, please log in again.");
                logout();
            })
            .finally(() => setIsLoading(false));
    }

    function submitListing(listingObject) {
        setIsLoading(true);
        const config = {
            headers: {Authorization: `Bearer ${user?.access_token}`},
        };
        axios.put('/listing/', listingObject, config)
            .then(() => {
                success("Listing posted successfully!");
                navigate("/", {replace: true});
            })
            .catch((err) => {
                error("An error occurred during logout");
                console.error(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function searchListingsLanding(searchText) {
        const config = {
            headers: {
                Authorization: `Bearer ${user?.access_token}`,
                'Content-Type': 'application/json'
            },
        };

        return axios.post('/search/text', {text: searchText}, config)
            .then((res) => res) // Return the entire response
            .catch((err) => {
                console.error("Search error:", err);
                throw err; // Re-throw to handle in the component
            });
    }

    function getListingData(listingID) {
        const config = {
            headers: {
                Authorization: `Bearer ${user?.access_token}`,
                'Content-Type': 'application/json'
            },
        };

        return axios.get(`/listing/${listingID}`, config)
            .then((res) => {
                if (!res.data) {
                    throw new Error("Listing data not found in response");
                }
                return res;
            })
            .catch((err) => {
                console.error("Error fetching listing:", err);
                if (err.response?.status === 404) {
                    throw new Error("Listing not found");
                } else if (err.response?.status === 401) {
                    throw new Error("Please log in to view this listing");
                } else {
                    throw new Error("Failed to fetch listing details");
                }
            });
    }

    return (
        <AuthContext.Provider
            value={{user, isAuthenticated, login, logout, signUp, submitListing, searchListingsLanding, getListingData}}
        >
            {children}
        </AuthContext.Provider>
    );
}

function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
}

export {AuthProvider, useAuth};
